import * as React from 'react';
import { useCallback, useContext, useState } from 'react';

import { useQuery } from '@apollo/client';

import type { Assignment, CourseBranch, CourseItem, PeerReview, Product } from '@coursera/event-pulse-types';

import { allowedFileTypesForPeerReviewAiGrading } from 'bundles/grading-common/utils/constants';
import type { Item } from 'bundles/learner-progress/types/Item';
import type {
  GetAiGradingEligibilityQuery,
  GetAiGradingEligibilityQueryVariables,
} from 'bundles/peer/api/__generated__/getAiGradingEligibility';
import getAiGradingEligibilityQuery from 'bundles/peer/api/getAiGradingEligibility.graphql';
import type { Submission } from 'bundles/peer/types/Submission';
import CenteredLoadingSpinner from 'bundles/ui/components/CenteredLoadingSpinner';

type PeerReviewEventingV3Data = {
  peerReview: PeerReview;
  assignment: Assignment;
  product: Product;
  courseItem: CourseItem;
  courseBranch: CourseBranch;
};

export type AiGradingContextType = {
  /**
   * Returns true if Assignment is eligible for AI Grading flow.
   */
  isAiGradingFlowPreview: () => boolean;

  peerReviewEventingV3Data: PeerReviewEventingV3Data;

  updateUnsupportedFileForAiGradingInSubmission: (fileUrl: string, unsupported: boolean) => void;
  doesSubmissionIncludeUnsupportedFileTypeForAiGrading: (submission: Submission) => boolean;
};

const AiGradingContext = React.createContext<AiGradingContextType | undefined>(undefined);

export function useAiGradingContext() {
  const context = useContext(AiGradingContext);
  if (context === undefined) {
    throw new Error(
      '`useAiGradingContext` was used outside of `AiGradingProvider`. Ensure your component is nested under `<AiGradingProvider>`.'
    );
  }
  return context;
}

export type AiGradingProviderProps = {
  computedItem: Item;
  assignmentId: string;
  assignmentVersion: number;
  courseBranchId: string;
  children: React.ReactNode;
};

const AiGradingProvider = ({
  computedItem,
  assignmentId,
  assignmentVersion,
  courseBranchId,
  children,
}: AiGradingProviderProps) => {
  const [loading, setLoading] = useState(true);
  const [unsupportedFilesForAiGradingInSubmission, setUnsupportedFilesForAiGradingInSubmission] = useState<
    Record<string, boolean>
  >({});

  const eligibilityResult = useQuery<GetAiGradingEligibilityQuery, GetAiGradingEligibilityQueryVariables>(
    getAiGradingEligibilityQuery,
    {
      variables: {
        assignmentId,
        assignmentVersion,
        courseId: computedItem.courseId,
        itemId: computedItem.id,
      },
      context: { clientName: 'gatewayGql' },
      onCompleted: () => setLoading(false),
      onError: () => setLoading(false),
    }
  );

  const isAiGradingFlowEligible = !!eligibilityResult.data?.PeerReviewAi?.getAiGradingEligibility?.isEligible;

  const isAiGradingFlowPreview = useCallback<() => $TSFixMe>(() => {
    return isAiGradingFlowEligible;
  }, [isAiGradingFlowEligible]);

  if (loading) {
    return <CenteredLoadingSpinner />;
  }

  const peerReviewEventingV3Data: PeerReviewEventingV3Data = {
    peerReview: {
      isAiGradingEligible: isAiGradingFlowPreview(),
    },
    assignment: {
      id: assignmentId,
      version: assignmentVersion,
    },
    product: {
      id: computedItem.courseId,
      type: 'course',
    },
    courseItem: {
      id: computedItem.id,
      type: 'peer_review',
      name: computedItem.name,
    },
    courseBranch: {
      id: courseBranchId,
    },
  };

  const updateUnsupportedFileForAiGradingInSubmission = (fileUrl: string, unsupported: boolean) => {
    setUnsupportedFilesForAiGradingInSubmission((prevState) => ({
      ...prevState,
      [fileUrl]: unsupported,
    }));
  };

  const doesSubmissionIncludeUnsupportedFileTypeForAiGrading = (submission: Submission) => {
    return Object.values(submission.definition.parts).some((part) => {
      if (part.typeName !== 'fileUpload') {
        return false;
      }
      const fileUrl = part.definition.fileUrl;
      const fileExtension = fileUrl.split('.').pop()?.toLowerCase() || '';

      // check if the file is marked as unsupported in the state
      if (unsupportedFilesForAiGradingInSubmission[fileUrl]) {
        return true;
      }

      // we still want to check the submission as there might be previous draft state sessions
      // allow empty file extension for AI grading because some questions have them as optional
      return fileExtension !== '' && !Object.keys(allowedFileTypesForPeerReviewAiGrading).includes(fileExtension);
    });
  };

  return (
    <AiGradingContext.Provider
      value={{
        isAiGradingFlowPreview,
        peerReviewEventingV3Data,
        updateUnsupportedFileForAiGradingInSubmission,
        doesSubmissionIncludeUnsupportedFileTypeForAiGrading,
      }}
    >
      {children}
    </AiGradingContext.Provider>
  );
};

export default AiGradingProvider;
